import React from 'react';
import { Table, Tooltip } from 'antd';
import { ArrowUp, ArrowDown, Package } from 'lucide-react';
import { EmptyState } from '../common';
import { semantic } from '../../theme/tokens';
import { formatPriceName } from '../../utils/priceName';

// Red = Mary dearer, green = Mary cheaper. Cell tints are inline because antd's own cell
// background would otherwise win over a utility class.
const RED_BG = semantic.danger.bg;
const GREEN_BG = semantic.success.bg;

const gk = (g) => (g == null ? '' : String(g));
const tintFor = (v) => (v == null || Number.isNaN(v) ? undefined : v >= 0 ? RED_BG : GREEN_BG);

const Dash = () => <span className="text-surface-400">—</span>;

/** Colored delta with an arrow: red ↑ (Mary dearer) / green ↓ (Mary cheaper). */
const Delta = ({ up, children }) => {
    const Arrow = up ? ArrowUp : ArrowDown;
    return (
        <span
            className={`inline-flex items-center justify-end gap-1 whitespace-nowrap font-semibold tabular-nums ${
                up ? 'text-danger-fg' : 'text-success-fg'
            }`}>
            {children} <Arrow className="text-[11px]" />
        </span>
    );
};

// "6 % ↑" (red = Mary dearer) / "-11 % ↓" (green = Mary cheaper).
const pctCell = (v) => {
    if (v == null || Number.isNaN(v)) return <Dash />;
    return <Delta up={v >= 0}>{Math.round(v)}%</Delta>;
};

// "$0.13 ↑" (red) / "($0.22) ↓" (green).
const difCell = (v) => {
    if (v == null || Number.isNaN(v)) return <Dash />;
    const up = v >= 0;
    const txt = up ? `$${v.toFixed(2)}` : `($${Math.abs(v).toFixed(2)})`;
    return <Delta up={up}>{txt}</Delta>;
};

const money = (v) =>
    v == null ? (
        <Dash />
    ) : (
        <span className="tabular-nums">${Number(v).toFixed(2)}</span>
    );

// Matrix cell: dollar difference + percentage, e.g. "$0.10  20% ↑" / "(-$0.10)  -20% ↓".
const matrixCell = (cell) => {
    if (!cell || cell.pct == null || Number.isNaN(cell.pct)) return <Dash />;
    const up = cell.pct >= 0;
    const dollar = up ? `$${cell.diff.toFixed(2)}` : `(-$${Math.abs(cell.diff).toFixed(2)})`;
    return (
        <Delta up={up}>
            {dollar} · {Math.round(cell.pct)}%
        </Delta>
    );
};

const tintCell = (v) => ({ style: { background: tintFor(v) } });
const rowClass = (r) => (r._total ? 'font-semibold' : '');

const SectionTitle = ({ children, hint }) => (
    <div className="mb-3">
        <h3 className="m-0 text-sm font-semibold text-surface-900">{children}</h3>
        {hint ? <p className="m-0 mt-0.5 text-xs text-surface-500">{hint}</p> : null}
    </div>
);

export default function PriceComparisonTables({
    mary = [],
    competition = [],
    compByGrams = {},
    maryOverallAvg = 0,
    compOverallAvg = 0
}) {
    if (!mary.length) {
        return (
            <EmptyState
                icon={<Package />}
                title="Sin productos Mary"
                description="Sin productos Mary para los filtros actuales. Filtra por categoría/segmento."
            />
        );
    }

    // PromComp for a Mary product = competition average of the SAME gram size.
    const promCompFor = (m) => {
        const v = compByGrams[gk(m.grams)];
        return v == null ? null : v;
    };

    // ---- Table 1: summary (PromMary vs same-grams PromComp) ----
    const summaryRows = mary.map((m, i) => {
        const pc = promCompFor(m);
        const index = pc ? (m.avgUsd / pc - 1) * 100 : null;
        return {
            key: `m-${i}`,
            name: formatPriceName(m),
            promMary: m.avgUsd,
            promComp: pc,
            index,
            dif: pc == null ? null : m.avgUsd - pc
        };
    });
    summaryRows.push({
        key: 'total',
        _total: true,
        name: 'Total',
        promMary: maryOverallAvg,
        promComp: compOverallAvg,
        index: compOverallAvg ? (maryOverallAvg / compOverallAvg - 1) * 100 : null,
        dif: maryOverallAvg - compOverallAvg
    });

    const summaryColumns = [
        {
            title: 'Producto',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 240,
            render: (t, r) => <span className={r._total ? 'font-semibold' : 'font-medium text-surface-900'}>{t}</span>
        },
        { title: 'Prom. Mary', dataIndex: 'promMary', key: 'promMary', align: 'right', width: 110, render: money },
        {
            title: <Tooltip title="Promedio de la competencia del mismo gramaje">Prom. Comp.</Tooltip>,
            dataIndex: 'promComp',
            key: 'promComp',
            align: 'right',
            width: 120,
            render: money
        },
        { title: 'Índice', dataIndex: 'index', key: 'index', align: 'right', width: 110, onCell: (r) => tintCell(r.index), render: pctCell },
        { title: 'Dif.', dataIndex: 'dif', key: 'dif', align: 'right', width: 120, onCell: (r) => tintCell(r.dif), render: difCell }
    ];

    // ---- Table 2: matrix (rows = competition, columns = Mary) ----
    const matrixColumns = [
        {
            title: 'Competidor',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 240,
            render: (t, r) => (
                <div className="leading-tight">
                    <div className={r._total ? 'font-semibold' : ''}>{t}</div>
                    {/* Show the competitor's own price — it's the reference each cell compares against. */}
                    {!r._total && r.price != null ? (
                        <div className="text-[11px] text-surface-400 font-normal tabular-nums">${Number(r.price).toFixed(2)}</div>
                    ) : null}
                </div>
            )
        },
        ...mary.map((m, i) => ({
            title: (
                <div className="leading-tight">
                    <div className="font-semibold">{formatPriceName(m)}</div>
                    <div className="text-[11px] text-surface-400 font-normal tabular-nums">${Number(m.avgUsd).toFixed(2)}</div>
                </div>
            ),
            dataIndex: `m${i}`,
            key: `m${i}`,
            align: 'right',
            width: 170,
            onCell: (r) => tintCell(r[`m${i}`]?.pct),
            render: matrixCell
        }))
    ];
    const cellFor = (mAvg, ref) =>
        ref ? { diff: mAvg - ref, pct: (mAvg / ref - 1) * 100 } : null;
    const matrixRows = competition.map((c, ci) => {
        const row = { key: `c-${ci}`, name: formatPriceName(c), price: c.avgUsd };
        mary.forEach((m, i) => {
            row[`m${i}`] = cellFor(m.avgUsd, c.avgUsd);
        });
        return row;
    });
    // Total row: each Mary product vs its same-grams competition average.
    const totalRow = { key: 'total', _total: true, name: 'Total (vs. competencia)' };
    mary.forEach((m, i) => {
        totalRow[`m${i}`] = cellFor(m.avgUsd, promCompFor(m));
    });
    matrixRows.push(totalRow);

    return (
        <div className="space-y-8">
            <div>
                <SectionTitle hint="Precio Mary vs. el promedio de la competencia del mismo gramaje.">
                    Resumen — Mary vs. competencia
                </SectionTitle>
                <Table
                    columns={summaryColumns}
                    dataSource={summaryRows}
                    rowClassName={rowClass}
                    size="small"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <div>
                <SectionTitle hint="Diferencia % de cada producto Mary (columnas) frente a cada competidor (filas). Rojo ↑ = Mary más caro; verde ↓ = Mary más barato.">
                    Diferencia por producto
                </SectionTitle>
                <Table
                    columns={matrixColumns}
                    dataSource={matrixRows}
                    rowClassName={rowClass}
                    size="small"
                    bordered
                    pagination={false}
                    scroll={{ x: 'max-content', y: 480 }}
                />
            </div>
        </div>
    );
}
